import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import PanelListWithImages from '../components/PanelListWithImages';
import PanelListLinks from '../components/PanelListLinks';
import PanelListText from '../components/PanelListText';
import PanelListVideos from '../components/PanelListVideos';
import JourneyOverview from '../components/JourneyOverview';
import StepCard from '../components/StepCard';
import StepHelpButton from '../components/StepHelpButton';
import MeetingsTabContent from '../components/MeetingsTabContent';
import Workshop2026MeetingsCard from '../components/Workshop2026MeetingsCard';
import { usePageTracking } from '../hooks/usePageTracking';
import { AdminService, ProgramConfig } from '../services/adminService';
import { getSiteConfig } from '../services/siteConfigService';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { useLocale } from '../context/LocaleContext';
import { resolveMessage } from '../i18n/translate';
import { getStepPageQuote } from '../config/stepPageCopy';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import styles from '../styles/saaHomePage.module.css';

/** Set true when ready to show the Meetings & Workshops tab on SAA home. */
const SHOW_SAA_MEETINGS_TAB = true;

interface SAAHomePageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const SAAHomePage: React.FC<SAAHomePageProps> = ({ user, onLogout, onLogin }) => {
  const { t, locale } = useLocale();
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [meetingsEnabled, setMeetingsEnabled] = useState(true);
  const [workshopsTabShowFullFinder, setWorkshopsTabShowFullFinder] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();
  
  // Determine active tab from URL or default. Tab order: Resources | Meetings | Work my steps. Default: Resources.
  const getInitialTab = (): 'tab1' | 'tab2' | 'tab3' => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'resources') return 'tab2';
    if (tabParam === 'meetings') return SHOW_SAA_MEETINGS_TAB ? 'tab3' : 'tab2';
    if (tabParam === 'steps') return 'tab1';
    return 'tab2'; // Resources is default for everyone
  };
  
  const [activeTab, setActiveTab] = useState<'tab1' | 'tab2' | 'tab3'>(getInitialTab());
  
  // Sync activeTab state when URL changes (for browser back/forward buttons)
  // Note: This only runs when searchParams changes, not when user state changes.
  // This ensures URL navigation (back/forward) works correctly while preserving
  // the user's current tab when they log in/out.
  useEffect(() => {
    if (!SHOW_SAA_MEETINGS_TAB && searchParams.get('tab') === 'meetings') {
      setSearchParams({ tab: 'resources' }, { replace: true });
      return;
    }
    const tabFromUrl = getInitialTab();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  // Handler for tab clicks - updates both state and URL. tab1=steps, tab2=resources, tab3=meetings.
  const handleTabClick = (tab: 'tab1' | 'tab2' | 'tab3') => {
    setActiveTab(tab);
    const tabParam = tab === 'tab1' ? 'steps' : tab === 'tab2' ? 'resources' : 'meetings';
    setSearchParams({ tab: tabParam });
  };
  
  // Step progress state (for Tab 1)
  const [stepProgress, setStepProgress] = useState<Record<number, ProgressItem | null>>({});
  const [journeyProgress, setJourneyProgress] = useState<JourneyProgress | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const stepProgressRef = useRef<Record<number, ProgressItem | null>>({});

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t('nav.home'), path: '/', active: false },
    { label: t('nav.saa'), path: '/saa', active: true }
  ];

  // Track page visit
  usePageTracking('SAA Homepage');

  useEffect(() => {
    perfMark('SAAHomePage', 'shell visible');
  }, []);

  // Load program config on mount; site config also refreshes when opening Meetings tab
  useEffect(() => {
    loadConfig();
  }, []);

  const refreshSiteConfig = useCallback(() => {
    getSiteConfig().then((site) => {
      setMeetingsEnabled(site.meetings_enabled);
      setWorkshopsTabShowFullFinder(!!site.workshops_tab_show_full_finder);
    });
  }, []);

  useEffect(() => {
    refreshSiteConfig();
  }, [refreshSiteConfig]);

  useEffect(() => {
    if (activeTab === 'tab3') {
      refreshSiteConfig();
    }
  }, [activeTab, refreshSiteConfig]);

  // Emergency override: allow a specific logged-in user to see meetings regardless
  // of the site-level meetings_enabled flag (useful for testing on localhost/prod).
  const meetingsGateBypass =
    (user?.email || '').toLowerCase() === 'matt.taylor64@gmail.com';
  const meetingsAllowed = meetingsGateBypass || meetingsEnabled;
  /** Full meeting finder hidden for launch; builder can re-enable via Admin site config while logged in as admin. */
  const showFullMeetingsFinder =
    meetingsAllowed && workshopsTabShowFullFinder && !!user && (user?.isAdmin || meetingsGateBypass);

  // Load step progress when user is available and tab1 is active (reload on navigation back)
  useEffect(() => {
    if (user && activeTab === 'tab1') {
      loadStepProgress();
    }
  }, [user, activeTab, location.pathname, location.search, stepWorkspaceRevision]);

  // Keep ref in sync with state
  useEffect(() => {
    stepProgressRef.current = stepProgress;
  }, [stepProgress]);

  const loadConfig = async () => {
    try {
      setConfigLoading(true);
      perfMark('SAAHomePage', 'loadConfig start');
      const programConfig = await perfTimeAsync('SAAHomePage loadConfig (background)', () =>
        AdminService.getProgramConfig('saa')
      );
      setConfig(programConfig);
      clearError();
      perfMark('SAAHomePage', 'config loaded');
    } catch (err: any) {
      console.error('Error loading SAA config:', err);
      handleError(err, t('saa_home.load_failed'));
    } finally {
      setConfigLoading(false);
    }
  };

  const loadStepProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);
    try {
      perfMark('SAAHomePage', 'loadStepProgress start');
      await perfTimeAsync('SAAHomePage loadStepProgress (total)', async () => {
        const { stepProgress: progress, journeyProgress: journey } =
          await progressService.loadStepsOverviewProgress('SAA', '12-steps');
        setStepProgress(progress);
        setJourneyProgress(journey);
      });
      perfSummary('SAA Home — Work my steps tab');
    } catch (err) {
      console.error('Error loading step progress:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user) return 'not-started';
    
    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    return 'not-started';
  };

  const handleStepClick = (stepNumber: number) => {
    navigate(`/saa/12-steps/step-${stepNumber}`);
  };

  const handleGettingStartedClick = () => {
    navigate('/saa/12-steps/getting-started');
  };

  const getGettingStartedStatus = (): 'not-started' | 'in-progress' | 'completed' => {
    return getStepStatus(0);
  };

  const renderPanel = (panel: any) => {
    switch (panel.format) {
      case 'list_with_images':
        return (
          <PanelListWithImages
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_links':
        return (
          <PanelListLinks
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_text':
        return (
          <PanelListText
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      case 'list_videos':
        return (
          <PanelListVideos
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
            iconUrl={panel.icon_url}
            accentColor={panel.accent_color}
            showItemCount={panel.show_item_count !== false}
          />
        );
      default:
        return null;
    }
  };

  // Step data with descriptions
  const steps = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const number = i + 1;
        return {
          number,
          title: getStepPageQuote(number),
          description: t(`working_steps.desc.${number}`),
          path: `/saa/12-steps/step-${number}`,
        };
      }),
    [t, locale]
  );

  const getLogoUrl = (): string | null => {
    if (!config?.logo_url) return null;
    let logoUrl = config.logo_url;
    if (logoUrl.includes('recoverycommons.com.s3.amazonaws.com')) {
      logoUrl = logoUrl.replace(
        'https://recoverycommons.com.s3.amazonaws.com/',
        'https://s3.amazonaws.com/recoverycommons.com/'
      );
    }
    return logoUrl;
  };

  /** Admin-edited text: show the translation only while Admin still has the English the catalog mirrors. */
  const adminText = (value: string | undefined, key: string): string => {
    if (!value) return '';
    if (locale === 'en') return value;
    return value.trim() === resolveMessage(key, { locale: 'en' }).trim() ? t(key) : value;
  };

  const saaIconUrl = config ? getLogoUrl() : null;
  const pageTitle = adminText(config?.title, 'saa_home.title') || 'SAA';
  const pageSubtitle = adminText(config?.subtitle, 'saa_home.subtitle');
  const resourcesTabName = adminText(config?.tab2?.name, 'saa_home.tab.resources') || t('saa_home.tab.resources');
  const meetingsTabName = adminText(config?.tab3?.name, 'saa_home.tab.meetings') || t('saa_home.tab.meetings');
  const resourcesHeader = adminText(config?.tab2?.header_text, 'saa_home.tab.resources_header');
  const meetingsHeader = adminText(config?.tab3?.header_text, 'saa_home.tab.meetings_header');

  return (
    <div className={styles.saaHomePage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      <main className={styles.mainContent}>
        {errorMessage && authError && (
          <div className={styles.errorState}>
            <AuthErrorDisplay
              error={errorMessage}
              onLogin={onLogin}
              onRefresh={() => window.location.reload()}
            />
          </div>
        )}

        {!authError && (
          <>
            {!configLoading && !config && errorMessage && (
              <div className={styles.errorState}>
                <p>{errorMessage}</p>
                <button type="button" onClick={loadConfig} className={styles.retryButton}>
                  {t('saa_home.retry')}
                </button>
              </div>
            )}

            <div className={styles.pageHeader}>
              <div className={styles.titleRow}>
                <h1 className={styles.saaTitle}>{pageTitle}</h1>
                {saaIconUrl && (
                  <img 
                    src={saaIconUrl} 
                    alt="SAA" 
                    className={styles.saaIcon}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className={styles.aboutContent}>
                {configLoading && !pageSubtitle ? (
                  <p className={styles.aboutPreviewText}>{t('saa_home.loading')}</p>
                ) : !aboutExpanded ? (
                  <>
                    <div className={styles.aboutPreview}>
                      <span className={styles.aboutPreviewText}>{pageSubtitle}</span>
                      {pageSubtitle && (
                        <>
                          {' '}
                          <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(true)}>
                            {t('saa_home.read_more')}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.aboutFull}>
                      <p>{pageSubtitle}</p>
                      <p>{t('saa_home.about.intro')}</p>
                      <ul className={styles.aboutList}>
                        <li><strong>{t('saa_home.about.resources_label')}</strong> - {t('saa_home.about.resources_body')}</li>
                        <li><strong>{t('saa_home.about.steps_label')}</strong> - {t('saa_home.about.steps_body')}</li>
                      </ul>
                      <p>{t('saa_home.about.not_allied')}</p>
                      <p>{t('saa_home.about.membership')}</p>
                    </div>
                    <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(false)}>
                      {t('saa_home.show_less')}
                    </button>
                  </>
                )}
                {locale !== 'en' && (
                  <p className={styles.languageNote}>
                    {t('saa_home.resources_english_note', { tab: resourcesTabName })}
                  </p>
                )}
              </div>
            </div>

            <div className={styles.tabsContainer}>
              <div className={styles.tabs}>
                <button
                  className={`${styles.tab} ${activeTab === 'tab2' ? styles.activeTab : ''}`}
                  onClick={() => handleTabClick('tab2')}
                  style={activeTab === 'tab2' && config?.tab2?.accent_color ? {
                    color: config.tab2.accent_color,
                    borderBottomColor: config.tab2.accent_color
                  } : {}}
                >
                  {config?.tab2?.icon_url && (
                    <img src={config.tab2.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }} />
                  )}
                  {resourcesTabName}
                </button>
                {SHOW_SAA_MEETINGS_TAB && (
                <button
                  className={`${styles.tab} ${activeTab === 'tab3' ? styles.activeTab : ''}`}
                  onClick={() => handleTabClick('tab3')}
                  style={activeTab === 'tab3' && config?.tab3?.accent_color ? {
                    color: config.tab3.accent_color,
                    borderBottomColor: config.tab3.accent_color
                  } : {}}
                >
                  {config?.tab3?.icon_url && (
                    <img src={config.tab3.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }} />
                  )}
                  {meetingsTabName}
                </button>
                )}
                <button
                  className={`${styles.tab} ${activeTab === 'tab1' ? styles.activeTab : ''}`}
                  onClick={() => handleTabClick('tab1')}
                  style={activeTab === 'tab1' && config?.tab1?.accent_color ? {
                    color: config.tab1.accent_color,
                    borderBottomColor: config.tab1.accent_color
                  } : {}}
                >
                  {config?.tab1?.icon_url && (
                    <img src={config.tab1.icon_url} alt="" className={styles.tabIcon} onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }} />
                  )}
                  {t('working_steps.tab.label')}
                </button>
              </div>

              {activeTab === 'tab1' && (
                <div className={styles.tabContent}>
                  <div className={styles.stepsTabHeader}>
                    <div className={styles.stepsTabTitleRow}>
                      <h2 className={styles.stepsTabTitle}>{t('working_steps.tab.title')}</h2>
                      <StepHelpButton stepKey="steps-tab" />
                    </div>
                  </div>

                  {isInitializing && user && (
                    <div className={styles.loadingMessage}>
                      <div>⏳ {t('working_steps.loading')}</div>
                      <div className={styles.loadingSubtext}>{t('working_steps.loading_sub')}</div>
                    </div>
                  )}

                  {!user && (
                    <div className={styles.loginPrompt}>
                      <div className={styles.loginPromptContent}>
                        <h2>{t('working_steps.tab.login_title')}</h2>
                        <p>{t('working_steps.tab.login_body')}</p>
                        <button onClick={onLogin} className={styles.loginButton}>
                          {t('working_steps.tab.login_cta')}
                        </button>
                      </div>
                    </div>
                  )}

                  {!isInitializing && user && (
                    <>
                      <JourneyOverview
                        user={user}
                        stepProgress={stepProgress}
                        journeyProgress={journeyProgress}
                      />
                      <div className={styles.stepsGrid}>
                        <StepCard
                          label={t('working_steps.gs.label')}
                          title={t('working_steps.gs.title')}
                          description={t('working_steps.gs.description')}
                          status={getGettingStartedStatus()}
                          isLoggedIn={!!user}
                          stepPath="/saa/12-steps/getting-started"
                          onStepClick={handleGettingStartedClick}
                          onLoginClick={onLogin}
                        />
                        {steps.map((step) => (
                          <StepCard
                            key={step.number}
                            stepNumber={step.number}
                            title={step.title}
                            description={step.description}
                            status={getStepStatus(step.number)}
                            isLoggedIn={!!user}
                            stepPath={step.path}
                            onStepClick={() => handleStepClick(step.number)}
                            onLoginClick={onLogin}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'tab2' && (
                <div className={styles.tabContent}>
                  {configLoading && (
                    <p className={styles.loadingMessage}>{t('saa_home.loading_resources')}</p>
                  )}
                  {!configLoading && config && (
                    <>
                      {resourcesHeader && (
                        <p className={styles.tab2Header}>{resourcesHeader}</p>
                      )}
                      <div className={styles.panelsContainer}>
                        {config.tab2?.panels
                          ?.sort((a: any, b: any) => a.order - b.order)
                          .map((panel: any) => renderPanel(panel))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'tab3' && (
                <div className={styles.tabContent}>
                  {/* Default: CMS header + active 2026 workshop only. Full finder is admin/builder opt-in. */}
                  {showFullMeetingsFinder ? (
                    <MeetingsTabContent
                      user={user}
                      onLogin={onLogin}
                      headerText={meetingsHeader || undefined}
                    />
                  ) : (
                    <>
                      {meetingsHeader && (
                        <p className={styles.tab2Header}>{meetingsHeader}</p>
                      )}
                      <Workshop2026MeetingsCard />
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SAAHomePage;
